

import React, { useEffect, useLayoutEffect, useState } from "react";
import WrapperModalPlan from "../../Components/WrapperPlan";
import ProposalCard from "../../Components/ProposalCard";
import { auth, db } from "../../utils/firebase";
import { useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/material/styles";
import { Button, Typography, Skeleton, Avatar } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CreateNewUserModal from "../../Components/CreateNewUserModal";
import AddEmployeeModal from "../../Components/AddEmployeeModal";
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import "./dashboard.css";
import { useSelector } from "react-redux";
import NavBar from "../../Components/NavBar";
import Collaborators from "../../Components/Collaborators";
import useMediaQuery from '@mui/material/useMediaQuery';
import DeleteIcon from "@mui/icons-material/Delete";
//imports needed for refetching data 
import getClientDetails from "../../utils/getClientDetails";
import { useDispatch } from "react-redux";
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle'; 
// Importing Firestore functions for fetching updated client data
import { doc, getDoc } from "firebase/firestore";

const getProposalName = (proposal) => proposal?.Grant_Proposal_Name || "Untitled Proposal";

const isNewProposal = (proposal) => {
  if (!proposal || !Object.prototype.hasOwnProperty.call(proposal, "New")) return false;
  const value = proposal.New;
  return value !== false && value !== null && value !== 0 && String(value).toLowerCase() !== "false";
};

const isProposalDueSoon = (proposal) => {
  if (!proposal?.Due_Date) return false;
  const dueDate = new Date(proposal.Due_Date);
  if (Number.isNaN(dueDate.getTime())) return false;

  const [hours, minutes] = proposal.Due_Time
    ? proposal.Due_Time.split(":").map(Number)
    : [0, 0];
  dueDate.setHours(hours || 0, minutes || 0, 0, 0);

  const timeDifferenceMillis = dueDate - new Date();
  return timeDifferenceMillis < 6 * 24 * 60 * 60 * 1000 && timeDifferenceMillis >= 0;
};

const formatProposalDueDate = (proposal) => {
  if (!proposal?.Due_Date) return "Not set";
  const dueDate = new Date(proposal.Due_Date);
  return Number.isNaN(dueDate.getTime()) ? "Not set" : dueDate.toLocaleDateString("en-US");
};

const AllClientsMain = styled("section")({
  height: "100%",
  minWidth: 0,
  overflow: "hidden",
  textAlign: "left",
});

const AllClientsSearchRow = styled("div")({
  minHeight: "65px",
  display: "flex",
  alignItems: "flex-end",
  justifyContent: "flex-start",
  padding: "0 16px 10px 21px",
  boxSizing: "border-box",
});

const AllClientsSearch = styled("label")({
  width: "min(700px, 72%)",
  height: "27px",
  display: "flex",
  alignItems: "center",
  padding: "0 10px",
  border: "2px solid #8502FF",
  borderRadius: "5px",
  backgroundColor: "#29005F",
  color: "#DBC5FF",
  '& input': {
    width: "100%",
    border: 0,
    outline: 0,
    backgroundColor: "transparent",
    color: "white",
  },
  '& input::placeholder': {
    color: "#C9B7E9",
    opacity: 1,
  },
});

const AllClientsTableBody = styled("div")({
  height: "100%",
  overflow: "auto",
  direction: "rtl",
  scrollbarGutter: "stable",
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",
  '&::-webkit-scrollbar': {
    width: "5px",
    height: "5px",
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: "transparent",
    borderRadius: "999px",
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: "transparent",
  },
});

const FilterButton = styled("button", {
  shouldForwardProp: (prop) => prop !== "isDue",
})(({ isDue }) => ({
  height: "28px",
  padding: "0 14px",
  border: `2px solid ${isDue ? "#FFB000" : "#8502FF"}`,
  borderRadius: "4px",
  backgroundColor: isDue ? "#A34F00" : "#380093",
  color: "white",
  cursor: "default",
  fontWeight: 400,
}));

const ProposalPreview = styled("div", {
  shouldForwardProp: (prop) => prop !== "statusVariant",
})(({ statusVariant }) => ({
  position: "relative",
  width: "156px",
  height: "24px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  padding: "0 7px",
  border: `2px solid ${statusVariant === "due" ? "#FFB000" : "#8502FF"}`,
  borderRadius: "3px",
  backgroundColor: statusVariant === "due"
    ? "#A34F00"
    : statusVariant === "new"
      ? "#380093"
      : "#AAAAAA",
  color: statusVariant === "due" || statusVariant === "new" ? "white" : "#6900DF",
  fontSize: "12px",
  cursor: "default",
  '& > span:first-of-type': {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  '&:hover, &:focus-visible': {
    borderColor: "#DBC5FF",
    outline: "none",
  },
  '&:hover .proposal-hover-details, &:focus-visible .proposal-hover-details': {
    display: "flex",
  },
}));

const ProposalInfo = styled("span")({
  width: "14px",
  height: "14px",
  flex: "0 0 14px",
  display: "grid",
  placeItems: "center",
  borderRadius: "50%",
  backgroundColor: "#8502FF",
  color: "white",
  font: "700 10px/1 serif",
});

const ProposalTooltip = styled("div")({
  position: "absolute",
  zIndex: 20,
  top: "calc(100% + 7px)",
  left: "50%",
  width: "220px",
  display: "none",
  transform: "translateX(-50%)",
  flexDirection: "column",
  gap: "4px",
  padding: "10px 11px 0",
  border: "2px solid #8502FF",
  borderRadius: "4px",
  backgroundColor: "#B9B9B9",
  color: "#5B00BD",
  boxShadow: "0 10px 28px rgba(0, 0, 0, 0.35)",
  fontSize: "12px",
  pointerEvents: "auto",
  '&::before': {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: "100%",
    width: "100%",
    height: "7px",
  },
  '& .proposal-view-button': {
    width: "calc(100% + 22px)",
    margin: "5px -11px 0",
    padding: "6px 11px",
    border: 0,
    borderTop: "2px solid #8502FF",
    backgroundColor: "#160032",
    color: "#D8BFFF",
    textAlign: "left",
    cursor: "pointer",
  },
});

function ProposalPreviewChip({ proposal, client, onViewProposal }) {
  const dueSoon = isProposalDueSoon(proposal);
  const newProposal = isNewProposal(proposal);
  const statusVariant = dueSoon ? "due" : newProposal ? "new" : "default";
  const assignedTo = proposal.Assigned || proposal.assigned || proposal.Assigned_To || proposal.assigned_to || "Unassigned";

  return (
    <ProposalPreview tabIndex="0" statusVariant={statusVariant}>
      <span>{getProposalName(proposal)}</span>
      <ProposalInfo aria-hidden="true">i</ProposalInfo>
      <ProposalTooltip className="proposal-hover-details" role="tooltip">
        <strong>{getProposalName(proposal)}</strong>
        <span>Due: {formatProposalDueDate(proposal)}</span>
        <span>Status: {newProposal ? "New" : "In progress"}</span>
        <span>Assigned: {assignedTo}</span>
        <button
          type="button"
          className="proposal-view-button"
          onClick={(event) => {
            event.stopPropagation();
            onViewProposal(client, proposal);
          }}
        >
          View proposal
        </button>
      </ProposalTooltip>
    </ProposalPreview>
  );
}

function AllClientsTablePreview({ clients, onBack, onViewProposal }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [showScrollbar, setShowScrollbar] = useState(true);
  const [scrollbarMetrics, setScrollbarMetrics] = useState({ top: 0, height: 0, left: 0, needed: false });
  const scrollbarTimer = React.useRef(null);
  const tableBodyRef = React.useRef(null);
  const normalizedQuery = searchQuery.trim().toLowerCase();
  const filteredClients = clients.reduce((matches, client) => {
    if (!normalizedQuery) {
      matches.push(client);
      return matches;
    }

    const clientMatches = (client.organization_name || "").toLowerCase().includes(normalizedQuery);
    const matchingProposals = client.proposals.filter((proposal) =>
      getProposalName(proposal).toLowerCase().includes(normalizedQuery)
    );

    if (clientMatches || matchingProposals.length > 0) {
      matches.push({
        ...client,
        proposals: clientMatches ? client.proposals : matchingProposals,
      });
    }

    return matches;
  }, []);

  useEffect(() => {
    scrollbarTimer.current = setTimeout(() => setShowScrollbar(false), 3000);
    return () => clearTimeout(scrollbarTimer.current);
  }, []);

  const updateScrollbarMetrics = (element) => {
    if (!element) return;
    const { clientHeight, scrollHeight, scrollTop, clientWidth, offsetWidth } = element;
    const needed = scrollHeight > clientHeight;
    const scrollbarGutterWidth = Math.max(5, offsetWidth - clientWidth);
    const arrowButtonHeight = scrollbarGutterWidth;
    const trackHeight = Math.max(0, clientHeight - (arrowButtonHeight * 2));
    const height = needed ? Math.min(trackHeight, Math.max(28, (trackHeight * clientHeight) / scrollHeight)) : 0;
    const availableTrack = Math.max(0, trackHeight - height);
    const left = Math.max(0, (scrollbarGutterWidth - 5) / 2);
    const top = needed && scrollHeight > clientHeight
      ? arrowButtonHeight + ((scrollTop / (scrollHeight - clientHeight)) * availableTrack)
      : arrowButtonHeight;
    setScrollbarMetrics({ top, height, left, needed });
  };

  useLayoutEffect(() => {
    updateScrollbarMetrics(tableBodyRef.current);
  }, [searchQuery, clients.length]);

  const handleTableScroll = (event) => {
    updateScrollbarMetrics(event.currentTarget);
    setShowScrollbar(true);
    clearTimeout(scrollbarTimer.current);
    scrollbarTimer.current = setTimeout(() => setShowScrollbar(false), 3000);
  };

  return (
    <>
      <div style={{ width: "2.857143%", flex: "0 0 2.857143%", backgroundColor: "#250062" }} />
      <div style={{ width: "7px", flex: "0 0 7px", backgroundColor: "#11002E" }} />
      <AllClientsMain aria-label="All clients preview" style={{ flex: 1 }}>
        <div
          style={{
            minHeight: "93px",
            display: "grid",
            gridTemplateColumns: "17.647059% 82.352941%",
            alignItems: "stretch",
          }}
        >
          <Typography
            sx={{ display: "flex", justifyContent: "center", position: "relative", top: "1.5rem", left: "-2px", alignSelf: "start" }}
            color="white"
          >
            Client List
          </Typography>
          <AllClientsSearchRow>
            <AllClientsSearch>
              <span className="sr-only">Search clients and proposals</span>
              <input
                type="search"
                placeholder="Search clients or proposals"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
              />
            </AllClientsSearch>
          </AllClientsSearchRow>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "17.647059% 82.352941%",
            height: "44px",
            alignItems: "center",
            backgroundColor: "#4D00CA",
            borderTop: "3px solid #5406D2",
            borderBottom: "3px solid #5406D2",
          }}
        >
          <div style={{ width: "97%", height: "100%", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <button
              type="button"
              onClick={onBack}
              style={{
                height: "100%",
                padding: "0 6px",
                border: 0,
                background: "transparent",
                color: "white",
                textAlign: "left",
                textDecoration: "underline",
                cursor: "pointer",
              }}
            >
              Dashboard View
            </button>
            <strong style={{ textAlign: "right" }}>Filter List</strong>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "36px", padding: "0 16px 0 21px" }}>
            <FilterButton type="button">New Proposals</FilterButton>
            <FilterButton type="button" isDue>Due Proposals</FilterButton>
          </div>
        </div>

        <div style={{ position: "relative", height: "calc(100% - 137px)" }}>
          <AllClientsTableBody ref={tableBodyRef} onScroll={handleTableScroll}>
            <div style={{ direction: "ltr", minWidth: "100%" }}>
          {filteredClients.map((client, clientIndex) => (
            <div
              key={client.client_id}
              style={{
                width: "100%",
                height: "42px",
                minHeight: "42px",
                maxHeight: "42px",
                display: "grid",
                gridTemplateColumns: "minmax(0, 17.647059%) minmax(0, 82.352941%)",
                alignItems: "center",
                backgroundColor: clientIndex % 2 === 0 ? "#2A006A" : "#10002D",
              }}
            >
              <div
                className="rounded-md h-[34px] text-white text-[15px] flex items-center px-2.5 border-3 bg-[#29005F] border-[#5406D2] border-[3px]"
                style={{
                  width: "97%",
                  height: "34px",
                  minHeight: "34px",
                  maxHeight: "34px",
                  margin: "0 auto",
                  boxSizing: "border-box",
                  overflow: "hidden",
                  minWidth: 0,
                }}
              >
                <span
                  title={client.organization_name}
                  style={{ display: "block", width: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                >
                  {client.organization_name}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "15px", padding: "0 16px" }}>
                {client.proposals.map((proposal, proposalIndex) => (
                  <ProposalPreviewChip
                    key={proposal.id || `${getProposalName(proposal)}-${proposalIndex}`}
                    proposal={proposal}
                    client={client}
                    onViewProposal={onViewProposal}
                  />
                ))}
              </div>
            </div>
          ))}
          {filteredClients.length === 0 && (
            <div style={{ padding: "18px 16px", color: "#C9B7E9", textAlign: "left" }}>
              No matching clients or proposals.
            </div>
          )}
            </div>
          </AllClientsTableBody>
          {scrollbarMetrics.needed && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                zIndex: 5,
                left: `${scrollbarMetrics.left}px`,
                top: `${scrollbarMetrics.top}px`,
                width: "5px",
                height: `${scrollbarMetrics.height}px`,
                borderRadius: "999px",
                backgroundColor: "#784DBD",
                opacity: showScrollbar ? 1 : 0,
                transition: "opacity 500ms ease",
                pointerEvents: "none",
              }}
            />
          )}
        </div>
      </AllClientsMain>
    </>
  );
}

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    // [0] is the first name, [length - 1] is the last name
    const firstLetter = parts[0][0];
    const lastLetter = parts[parts.length - 1][0];
    return (firstLetter + lastLetter).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

function Main() {
  const isSmallScreen = useMediaQuery('(max-width:640px)');
  const isMediumScreen = useMediaQuery('(max-width:1024px)');
  const isLargeScreen = useMediaQuery('(min-width:1025px) and (max-width:1440px)');
  const isXLargeScreen = useMediaQuery('(min-width:1441px)');

  const clientsStore = useSelector(state => state.clients);
  const clientsLoaded = Boolean(
    clientsStore.userId
    && Object.prototype.hasOwnProperty.call(clientsStore, clientsStore.userId)
  );
  const loadedClients = clientsLoaded ? clientsStore[clientsStore.userId] : {};
  const activeClients = Object.values(loadedClients).filter(client => !client.is_delete);
  const allClientsForView = Object.entries(loadedClients)
    .filter(([_, client]) => !client.is_delete)
    .map(([client_id, client]) => ({
      ...client,
      client_id,
      proposals: Array.isArray(client.proposals)
        ? client.proposals
          .filter((proposal) => proposal.is_deleted === undefined || !proposal.is_deleted)
          .sort((a, b) => {
            const firstDueDate = new Date(a.Due_Date).getTime();
            const secondDueDate = new Date(b.Due_Date).getTime();
            if (Number.isNaN(firstDueDate)) return 1;
            if (Number.isNaN(secondDueDate)) return -1;
            return firstDueDate - secondDueDate;
          })
        : [],
    }))
    .sort((a, b) => (a.organization_name || "").localeCompare(
      b.organization_name || "",
      undefined,
      { numeric: true, sensitivity: "base" }
    ));
  console.log("Active Clients Count:", activeClients.length);
  const [data, setData] = useState([]);
  const [clients, setClients] = useState({});
  const [clientNames, setClientNames] = useState([]);
  const location = useLocation();
  const [currentClient, setCurrentClient] = useState("");
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [editClientData, setEditClientData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [uid, setUid] = useState('');
  const [dashboardView, setDashboardView] = useState('client');
  
  // State for employee editing
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const navigate = useNavigate();
  const baseurl = process.env.REACT_APP_API_URL;
 
  //refetching data on page reload
  const dispatch = useDispatch()
  useEffect(() => {
    getClientDetails(dispatch)
  }, [dispatch]) // Add dispatch to dependency array
  //MUI styles 
  const AddClientButton = styled(Button)({
    height: "45px",
    color: "white",
    width: "97%",
    fontSize: "18px",
    backgroundColor: "#380093",
    border: "solid 3px #5406D2",
    margin: "5px 0",
  });

  const ViewAllClientButton = styled(Button)({
    backgroundColor: "#380093",
    border: "solid 3px #5406D2",
    width: "97%",
    height: "45px",
    color: "white",
    textTransform: "none",
  });

  const [open, setOpen] = React.useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  function openModalAtBegenning(names){
    console.log('Entering Here')
    console.log(names)
     if(names.length===0){
      openModal(true)
     } 
  }

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        navigate("/login");
      } else {
        const uid = user.uid;
        setUid(uid);
        await readData(uid, "");
      }
    });

  
    return () => unsubscribe();
  }, [navigate, clientsStore]);



  /* // Old navigation-based logic is commented out here
  useEffect(() => {
    if (location.state?.currentClient) {
      setCurrentClient(location.state.currentClient)
    }
    if (location.state?.organization_name) {
      setClientNames(location.state.organization_name)
      openModalAtBegenning(location.state.organization_name)
    }
  }, [location.state])
  */


  useEffect(() => {
    const lastClient = localStorage.getItem('LastClient');
    const clientIds = Object.keys(clientsStore);

    // If there is at least one client
    if (clientIds.length > 1) {
      // If the user has only one client
      if (clientIds.length === 1) {
        setCurrentClient({ ...clientsStore[clientIds[0]][Object.keys(clientsStore[clientIds[0]])[0]], client_id: Object.keys(clientsStore[clientIds[0]])[0] });
      }
      // If the user has more than one client
      else {
        // Check if the lastClient exists in the clientsStore
        const foundLastClient = clientIds.some(
          clientId => clientsStore[clientId][lastClient]
        );

        if (lastClient && foundLastClient) {
          setCurrentClient({ ...clientsStore[clientIds[1]][lastClient], client_id: lastClient });
        } else {
          // Fallback to setting the first available client if no last client is found
          setCurrentClient({ ...clientsStore[clientIds[1]][Object.keys(clientsStore[clientIds[1]])[0]], client_id: Object.keys(clientsStore[clientIds[1]])[0] });
        }
      }
    }

    
   
  }, [clientsStore]);

 // Automatically triggers the 'Create Client' modal for new users or those with no active data.
// It monitors the client storage and opens the onboarding popup if the filtered list is empty.
// useEffect(() => {
//   const storeKeys = Object.keys(clientsStore);

//   // We check for > 1 because your Redux store structure uses the first key for metadata
//   if (storeKeys.length > 1) {
//     const clientDataKey = storeKeys[1];
//     const activeClients = clientsStore[clientDataKey]
//       ? Object.values(clientsStore[clientDataKey]).filter(client => !client.is_delete)
//       : [];

//     if (activeClients.length === 0) {
//       setOpenModal(true);
//     } else {
//       // IMPORTANT: If clients exist, ensure the modal is closed
//       setOpenModal(false);
//     }
//   }
// }, [clientsStore]);
useEffect(() => {
  // The initial Redux state only contains metadata. Wait for the client request to
  // finish so that loading is not mistaken for a user with no clients.
  if (!clientsLoaded) {
    return;
  }

  setOpenModal(activeClients.length === 0);
}, [clientsLoaded, activeClients.length]);

 
  async function readData(uid, client_id) {
    try {
      const response = await fetch(`${baseurl}/get-user-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ uid: uid }),
      });

      const sortedData = await response.json();
      const clientsData = [];

      if (sortedData.sub_collections && Object.keys(sortedData.sub_collections).length > 0) {
        const clientID = Object.keys(sortedData.sub_collections)[0];

        if ((currentClient && currentClient === clientID) || clientID === client_id) {
          for (const [clientId, clientArray] of Object.entries(sortedData.sub_collections)) {
            clientArray.filter(client => !client.is_delete).forEach(client => {
              clientsData.push({ id: clientId, ...client });
            });
          }
        }
      }

      setData(clientsData.length > 0 ? clientsData : []);  // Set data or clear if empty
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }



  const handleNew = () => {
    if (currentClient === "") {
      alert("Select/click a client to add the new proposal.");
    } else {
      navigate("/new-proposal-dashboard", { state: { currentClient } });
    }
  };

  // Function to handle employee edit click
  const handleEditEmployee = (employee) => {
    setSelectedEmployee(employee);
    setShowEmployeeModal(true);
  };

  // Function to refresh UI after employee update by re-fetching the specific client document
  const onUpdateSuccess = async () => {
    if (auth.currentUser && currentClient?.client_id) {
      try {
        const docRef = doc(db, "Users", auth.currentUser.uid, "Client_Organization", currentClient.client_id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const updatedData = docSnap.data();
          setCurrentClient(prev => ({ ...prev, ...updatedData }));
          // Also update the global store to ensure consistency
          getClientDetails(dispatch);
        }
      } catch (error) {
        console.error("Error refreshing client data:", error);
      }
    }
  };

  const handleClick = (formDetails) => {
    // Navigate with URL parameters
    navigate(`/grant-assistant/${currentClient.client_id}/${formDetails.id}`, {
      state: {
        formDetails,
        currentClient,
        from: "proposal-dashboard"
      }
    });
  };

  const handleAllClientsProposalClick = (client, formDetails) => {
    navigate(`/grant-assistant/${client.client_id}/${formDetails.id}`, {
      state: {
        formDetails,
        currentClient: client,
        from: "proposal-dashboard"
      }
    });
  };

  const [clientToDelete, setClientToDelete] = useState(null);

  const handleDeleteClick = (client) => {
    setClientToDelete(client);
    setOpen(true); 
  };

  const handleDelete = async (client) => {
    if (!clientToDelete) return;
    setOpen(false);
    try {
      const response = await fetch(`${baseurl}/delete-organization`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, client_id: client.client_id, organization_name: client.organization_name }),
      });

      if (!response.ok) {
        throw new Error("Failed to delete client");
      }

      // Ensure the Redux store is updated first
      dispatch({ type: 'REMOVE_CLIENT', payload: client.client_id });

      // Update local state based on the updated Redux store
      const updatedClientsStore = { ...clientsStore };
      delete updatedClientsStore[client.client_id];

      setClients(updatedClientsStore);

      const remainingClients = Object.values(updatedClientsStore).flatMap(Object.values);

      if (remainingClients.length > 0) {
        setCurrentClient(remainingClients[0]);
        localStorage.setItem('LastClient', remainingClients[0].client_id);
      } else {
        setCurrentClient(null);
        localStorage.removeItem('LastClient');
      }


      await getClientDetails(dispatch);
    } catch (error) {
      console.error("Error deleting client:", error);
    }
  };

  return (
    <div className="overflow-hidden max-h-screen">
      <NavBar />

      <div className="dash-2 top-12">
        {dashboardView === 'all' ? (
          <AllClientsTablePreview
            clients={allClientsForView}
            onBack={() => setDashboardView('client')}
            onViewProposal={handleAllClientsProposalClick}
          />
        ) : (
        <>
        <div className={dashboardView === 'all'
          ? "w-1/5 bg-[#11002e] overflow-auto flex"
          : "w-1/5 bg-[#11002e] border-r-4 border-[#5406D2] overflow-auto flex"}>
          <div className="w-1/6 bg-[#250062] flex">
            {/* <Fab size="small"></Fab> */}
          </div>
          <div className={dashboardView === 'all' ? 'w-full flex-col all-clients-sidebar' : 'w-full flex-col space-y-12'}>
            <Typography
              className={dashboardView === 'all' ? 'all-clients-sidebar-title' : ''}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: dashboardView === 'all' ? "center" : undefined,
                position: dashboardView === 'all' ? "static" : "relative",
                top: dashboardView === 'all' ? undefined : "1.5rem",
                minHeight: dashboardView === 'all' ? "103px" : undefined,
              }}
              id="modal-title"
              color="white"
            >
              Client List 
            </Typography>

            {dashboardView === 'client' ? (
            <>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0" }}>
                <AddClientButton
                  type="button"
                  onClick={() => {
                    setEditClientData(null);
                    setOpenModal(true);
                  }}
                >
                  <AddCircleOutlineIcon />
                  <div className="text-[16px] pl-1 font-thin lowercase rounded-md"> <span className="uppercase">C</span>reate <span className="uppercase">C</span>lient</div>
                </AddClientButton>
                <ViewAllClientButton
                  type="button"
                  onClick={() => setDashboardView('all')}
                >
                  View All Clients
                </ViewAllClientButton>
              </div>

            <CreateNewUserModal
              editClientData={editClientData}
              open={openModal}
              onClose={(event, reason) => {
                // If the user has 0 clients and clicks the background (backdrop), 
                // we return early and do NOTHING (the modal stays open).
              
              if (activeClients.length === 0 && reason === 'backdropClick') {
      return;
    }
    // Otherwise, for returning users, it closes normally.
    setOpenModal(false);
  }}
  clients={clients}
  setClients={setClients}
  isFirstTime={activeClients.length === 0}
/>
            <div className="client-list relative bottom-[1.77rem]">
              {!clientsStore || Object.keys(clientsStore).length <= 1 ? (
                // Display Skeleton components while clientsStore is not loaded
                <div className="flex flex-col items-center justify-center">
                  <Skeleton sx={{ bgcolor: '#5406D2', p: 2.3, my: 0.25, border: '784DBD' }} variant="rounded" height={14} width="97%" />
                  <Skeleton sx={{ bgcolor: '#5406D2', p: 2.3, my: 0.25, border: '784DBD' }} variant="rounded" height={14} width="97%" />
                  <Skeleton sx={{ bgcolor: '#5406D2', p: 2.3, my: 0.25, border: '784DBD' }} variant="rounded" height={14} width="97%" />
                </div>
              ) : (
                (() => {
                  const secondKey = Object.keys(clientsStore)[1];
                  const clients = clientsStore[secondKey]
                    ? Object.entries(clientsStore[secondKey]).filter(([_, client]) => !client.is_delete).map(([client_id, client]) => ({
                      ...client,
                      client_id, // Add client_id to the client object
                    })).sort((a, b) => a.organization_name.localeCompare(b.organization_name, undefined, { numeric: true, sensitivity: 'base' }))
                    : [];

                  return clients.length > 0 ? (
                    clients.filter(client => !client.is_delete).map(client => (

                      <div
                        key={client.client_id}
                        className={`cursor-pointer rounded-md h-[34px] w-full text-white text-[15px] my-1 flex justify-between items-center px-2.5 border-3 ${currentClient?.organization_name && currentClient.client_id === client.client_id
                          ? 'bg-[#4D00CA] border-[#DBC5FF] border-[3px]'
                          : 'bg-[#29005F] border-[#5406D2] border-[3px]'
                          }`}
                        // When a client in the sidebar is clicked:
                        onClick={() => {
                          // 1. Set it as the currently active client for the main dashboard view.
                          setCurrentClient({ ...client, client_id: client.client_id });
                          localStorage.setItem('LastClient', client.client_id);
                        }}
                      >
                        {/* Container to group the Avatar and the client name together */}
                        <div className="flex items-center gap-2">
                          <p title={client.organization_name} className="pointer-events-none">
  {isSmallScreen
    ? client.organization_name.length > 10
      ? `${client.organization_name.slice(0, 5)}...`
      : client.organization_name
    : isMediumScreen
      ? client.organization_name.length > 12
        ? `${client.organization_name.slice(0, 10)}...`
        : client.organization_name
      : isLargeScreen
        ? client.organization_name.length > 25
          ? `${client.organization_name.slice(0, 15)}...`
          : client.organization_name
        : isXLargeScreen
          ? client.organization_name.length > 40
            ? `${client.organization_name.slice(0, 20)}...`
            : client.organization_name
          : client.organization_name}
                          </p>
                        </div>

                        <div>
                          <DeleteIcon
                            fontSize="small"
                            // We stop propagation here to prevent the parent button's onClick
                            // from firing, which would open the edit modal when we just want to delete.
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(client)
                              setOpen(true)
                            }}

                          ></DeleteIcon>
                        </div>
                        <Dialog
                          open={open}
                          onClose={handleClose}
                          sx={{
                            "& .MuiDialog-paper": {
                              backgroundColor: "black",
                              border: "1px solid white",
                              color: "white",
                            },
                          }}
                        >
                          <DialogTitle>
                            {`Do you want to delete client "${clientToDelete?.organization_name}"?`}
                          </DialogTitle>
                          <DialogContent>
                            <Typography>
                              Deleting this client will mark it as deleted and it will not be visible anymore.
                            </Typography>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={handleClose} sx={{
                              color: "white",
                              backgroundColor: "#382759",
                              border: "1px solid white",
                              "&:hover": { backgroundColor: "#1E093F" },
                            }}>CANCEL</Button>
                            <Button onClick={() => handleDelete(clientToDelete)} sx={{
                              color: "white",
                              backgroundColor: "red",
                              "&:hover": { backgroundColor: "#b71c1c" },
                            }} variant="outlined">
                              DELETE
                            </Button>
                          </DialogActions>
                        </Dialog>
                      </div>

                    ))
                  ) : (
                    // Fallback if clients array is empty
                    <div className="flex flex-col items-center justify-center text-white">
                      {/* {clientNames.map((clientName, index) => (
                        <div
                          key={index}
                          className="rounded-md h-[34px] w-[98.5%] text-white text-[15px] my-0.5 flex items-center px-2.5 border-3 bg-[#380093] border-[#5406D2] border-[3px]"
                          onClick={() => {
                            setCurrentClient({ organization_name: clientName, client_id: clientName });
                            localStorage.setItem('LastClient', clientName);
                          }}
                        >
                          <p>{clientName}</p>
                        </div>
                      ))} */}
                    </div>
                  );
                })()
              )}
            </div>
            </>
            ) : (
            <>
              <ViewAllClientButton
                type="button"
                aria-pressed="true"
                onClick={() => setDashboardView('client')}
              >
                View All Clients
              </ViewAllClientButton>
              <div style={{ width: "97%", margin: "4px auto 0", overflowY: "auto" }}>
                {allClientsForView.map((client) => (
                  <div
                    className="rounded-md h-[34px] w-full text-white text-[15px] my-1 flex items-center px-2.5 border-3 bg-[#29005F] border-[#5406D2] border-[3px]"
                    key={client.client_id}
                  >
                    {client.organization_name}
                  </div>
                ))}
              </div>
            </>
            )}
          </div>
        </div>
        <div className={dashboardView === 'all' ? "w-4/5 bg-[#11002e]" : "w-4/5 ml-3 bg-[#11002e]"}>
          {dashboardView === 'all' ? (
            <AllClientsMain aria-label="All clients preview">
              <AllClientsSearchRow className="mb-4 mt-3">
                <AllClientsSearch>
                  <span className="sr-only">Search clients and proposals</span>
                  <input type="text" placeholder="Search" readOnly />
                </AllClientsSearch>
              </AllClientsSearchRow>

              <div className="inner-section mb-4 flex items-center" style={{ justifyContent: "flex-start", gap: "38px", whiteSpace: "nowrap" }}>
                <strong>Filter List</strong>
                <FilterButton type="button">New Proposals</FilterButton>
                <FilterButton type="button" isDue>Due Proposals</FilterButton>
              </div>

              <div style={{ height: "calc(100% - 153px)", minWidth: 0, overflow: "auto" }}>
                {allClientsForView.map((client, clientIndex) => (
                  <div
                    key={client.client_id}
                    style={{
                      minWidth: "max-content",
                      height: "42px",
                      display: "flex",
                      alignItems: "center",
                      gap: "24px",
                      padding: "0 16px",
                      borderBottom: "1px solid #380093",
                      backgroundColor: clientIndex % 2 === 0 ? "#18003A" : "#21004D",
                    }}
                  >
                    {client.proposals.map((proposal, proposalIndex) => (
                      <ProposalPreviewChip
                        key={proposal.id || `${getProposalName(proposal)}-${proposalIndex}`}
                        proposal={proposal}
                        client={client}
                        onViewProposal={handleAllClientsProposalClick}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </AllClientsMain>
          ) : (
          <>
          <div className=" mb-4 mt-3  lg:flex md:flex items-center justify-between ">
          
                <WrapperModalPlan>
            <div className="flex justify-start ">
             
            <button
            onClick={handleNew}
             className=" dashbord-button3 button3 "
            >
              <AddCircleOutlineIcon />
              <div className="pl-1  "   style={{ fontFamily: 'Radley, serif'  }} >Create New Proposal</div>
            </button>
           
             </div>
          
            </WrapperModalPlan>
            
            <div className=" mt-2 flex justify-end lg:bottom-0 md:bottom-0 top-4 absolute right-6">
              {/* user collab */}
              <Collaborators headerText={"User Collaborator"} type={"User_Collaborator"} />
            </div>
          </div>

          <div className="inner-section mb-4 flex justify-between items-center">
            <p className="font-thin">Your proposals are in order by due date { }</p>
            <div className="flex items-center gap-3 relative z-50">
              {currentClient && currentClient.organization_name && (
                <>
                  <Avatar
                    className="dash-avatar avatar-org"
                    onClick={() => {
                      setEditClientData(currentClient);
                      setOpenModal(true);
                    }}
                  >
                    {getInitials(currentClient.organization_name)}
                  </Avatar>

                  {/* Dynamic Employee Avatars Group */}
                  <div className="flex gap-1">
                    {currentClient?.employees?.map((employee, index) => (
                      <Avatar
                        key={index}
                        className="dash-avatar avatar-emp"
                        title={`${employee.fullName} - ${employee.role}`}
                        onClick={() => handleEditEmployee(employee)} // Click handler to edit employee
                      >
                        {getInitials(employee.fullName)}
                      </Avatar>
                    ))}
                  </div>

                  <AddCircleOutlineIcon
                    className="cursor-pointer text-white ml-2"
                    sx={{ fontSize: 28 }}
                    onClick={() => {
                      setSelectedEmployee(null); // Clear any previously selected employee
                      setShowEmployeeModal(true);
                    }}
                  />
                </>
              )}
              <div className="font-thin relative bottom-1">
                {/* client collab */}
                {
                  currentClient && currentClient.organization_name ? (
                    <Collaborators headerText={"Client Collaborator"} type={"Client_Collaborator"} currentClient={currentClient} />
                  ) : null
                }
              </div>
            </div>
          </div>
          <div className="flex w-full mt-1">
            <div className="dash p-1 d-flex items-center gap-3 bg-red-400">
              {/* loads the add proposal when there is no proposals var in the currentClientobj  */}
            
              {/* loads skeleton when there is proposals var in the currentClientobj but proposals is 0 because it is loaing */}
              {(currentClient && currentClient.hasOwnProperty('proposals') && currentClient.proposals.length === 0 && !clientsStore || Object.keys(clientsStore).length <= 1) ? (
                <div className="flex flex-row gap-3 w-full">
                  <Skeleton sx={{ p: 4, bgcolor: '#3b265c' }} variant="rounded" height={160} width={260} />
                  <Skeleton sx={{ p: 4, bgcolor: '#3b265c' }} variant="rounded" height={160} width={260} />
                  <Skeleton sx={{ p: 4, bgcolor: '#3b265c' }} variant="rounded" height={160} width={260} />
                </div>
              ) : currentClient.hasOwnProperty('proposals') && currentClient.proposals.length > 0 ? (
                <>
                  {currentClient?.proposals && Array.isArray(currentClient.proposals) &&
                    [...currentClient.proposals].sort((a, b) => new Date(a.Due_Date) - new Date(b.Due_Date))
                      .map((item, index) => {
                        if ((item.is_deleted === undefined || !item.is_deleted)) {

                          return (
                            <ProposalCard
                              key={index}
                              item={item}
                              index={index}
                              handleClick={() => handleClick(item)}
                              clientID={currentClient.client_id}
                            />
                          );
                        }
                        return null; // If `is_deleted`, render nothing for this proposal.
                      })}
                </>
              ) : (
                <h1><KeyboardDoubleArrowLeftIcon /> Click on New Proposal Dashboard to create your first Proposal!</h1>
              )}
            </div>
          </div>
          </>
          )}
        </div>
        </>
        )}
      </div>
      <AddEmployeeModal
         open={showEmployeeModal}
         onClose={() => setShowEmployeeModal(false)}
         currentClient={currentClient}
         onUpdateSuccess={onUpdateSuccess}
         employeeData={selectedEmployee}
      />
    </div>
  );
}

export default Main;
